'use client';

import { BenefitComparisonTable } from '@hyphengroup/cms-components';
import type { BenefitComparisonTier } from '@hyphengroup/cms-components/cms-blocks/BenefitComparisonTable/types';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { pushDataLayer } from '../../pushDataLayer';
import { usePageTiTiers } from '../travel/useTiTiers';
import Translations from './i18n';
import type { CmsBenefitComparisonTableComponent } from '$/(cms)/_modules/cmsService/types/components';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type BenefitComparisonTableWrapperProps = {
  component: CmsBenefitComparisonTableComponent;
};

/**
 * Benefit (TI) comparison table wrapper (CMS-1642, Module 4). Brand-gated to
 * SingSaver (SG) and MoneyHero (HK) — money101 / moneymax render nothing.
 *
 * The benefit-table CMS component is structure-only (categories/attributes). The
 * tier columns are M3b's tiers from `usePageTiTiers` — the active trip type's
 * product set (each product carries the `attributes` the table joins on), priced
 * through the calculated-attributes engine's `TI_PREMIUM_PRICE` and sorted by
 * price — so a single ↔ annual toggle re-renders with a new product set and
 * column count against the same category structure, and M3b's effective tier
 * names the highlighted column: the two blocks always mark the same tier
 * (CMS-2054). The block prints the price as M3's tier cards do.
 */
export const BenefitComparisonTableWrapper = ({
  component,
}: BenefitComparisonTableWrapperProps): JSX.Element | null => {
  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);
  const {
    tiers: pageTiers,
    effectiveTierId,
    isAnnual,
    currencyCode,
  } = usePageTiTiers();

  if (context.brand !== 'singsaver' && context.brand !== 'moneyhero') {
    return null;
  }

  const period = `${currencyCode} / ${tr(isAnnual ? 'perYear' : 'perTrip', staticText)}`;

  const tiers = pageTiers.map(
    ({ id, product, price, isPriceLoading }): BenefitComparisonTier => ({
      id,
      // The full product name: M3b's `displayNameOverride` is a card label.
      name: product.product_name || product.title || '',
      price,
      // Show the period with a resolved price, or while the price skeleton is
      // loading (so "SGD / trip" sits under the shimmer, per the Figma) — but
      // not for a settled, unpriced/unavailable tier.
      period: price || isPriceLoading ? period : undefined,
      isPriceLoading,
      product,
    }),
  );

  return (
    <BenefitComparisonTable
      component={component}
      tiers={tiers}
      selectedTier={effectiveTierId}
      pushDataLayer={pushDataLayer}
    />
  );
};
